import { getAlgoliaResults } from '@algolia/autocomplete-js';
import { createQuerySuggestionsPlugin } from '@algolia/autocomplete-plugin-query-suggestions';

import {
  SEARCH_DEBOUNCE_DELAY,
  SURVEY_SUGGEST_TEXT
} from '@src/config/constants';
import { CollectionCategoryType } from '@src/generated/hooks';
import {
  buildCategoryFilterString,
  getAlgoliaClient,
  isQueryInResults,
  ProductHit
} from '@src/utils/algolia';

export interface AlgoliaProductsProps {
  category: CollectionCategoryType;
  categoryFilters: CollectionCategoryType[];
  onSelect: (event: OnAlgoliaProductInputChange) => void;
}

export interface OnAlgoliaProductInputChange {
  category: CollectionCategoryType;
  inputValue: string;
  productId: string;
}

/**
 * @name getAlgoliaProducts
 * @description Creates and returns a plugin that can be used with the
 * TypeAlgolia autocomplete component. This plugin handles all the specifics of
 * working with our 'products' index.
 */
export const getAlgoliaProducts = (props: AlgoliaProductsProps) => {
  const { category, categoryFilters, onSelect } = props;

  let timer: NodeJS.Timeout;

  const searchClient = getAlgoliaClient();

  const querySuggestionsPlugin = createQuerySuggestionsPlugin<ProductHit>({
    indexName: 'products',
    searchClient,
    transformSource({ source, state }) {
      return {
        ...source,

        /**
         * Only start the survey once we have 3 characters. This helps give
         * better results in the search.
         */
        getItems: ({ query }) => {
          // Don't query when the query is too short to be useful
          if (query.length < 3) return [];

          return new Promise((resolve) => {
            if (timer) clearTimeout(timer);

            timer = setTimeout(() => {
              resolve(
                getAlgoliaResults({
                  queries: [
                    {
                      indexName: 'products',
                      params: {
                        attributesToHighlight: ['full_name'],
                        attributesToRetrieve: ['full_name'],
                        filters: buildCategoryFilterString(categoryFilters),
                        hitsPerPage: 6,
                        restrictSearchableAttributes: ['full_name']
                      },
                      query: state.query
                    }
                  ],
                  searchClient,

                  /**
                   * Seems like only hits[0] contains actual results from algolia.
                   */

                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  transformResponse: ({ hits }) => {
                    const results = hits[0];

                    if (results.length <= 5 && !isQueryInResults(hits, query)) {
                      results.push({
                        full_name: `${SURVEY_SUGGEST_TEXT}${query}`,
                        objectID: ''
                      } as any);
                    }

                    return results;
                  }
                })
              );
            }, SEARCH_DEBOUNCE_DELAY);
          });
        },

        /**
         * Although we are listening for the "input" event on the input box in
         * the <ProductInput/> component, it doesn't fire when someone selects
         * an option from the dropdown. So we need this in addition to
         * listening for individual characters.
         */
        onSelect: ({ item, setQuery }) => {
          const inputValue = item.full_name.replace(SURVEY_SUGGEST_TEXT, '');

          onSelect({
            category,
            inputValue,
            productId: item.objectID ?? ''
          });

          // This is to prevent the input box from resetting
          setQuery(inputValue);
        },

        /**
         * This is used to just return the name of the product as the only
         * thing in the dropdown.
         */

        templates: {
          ...source.templates,
          item({ item, components, html }) {
            if (item.full_name.includes(SURVEY_SUGGEST_TEXT)) {
              return html`<div className="italic text-14 sm:text-16 ml-1xs">
                ${item.full_name}
              </div>`;
            }

            return html`<div className="text-14 sm:text-16 ml-1xs">
              ${components.Highlight({
                attribute: 'full_name',
                hit: item,
                tagName: 'b'
              })}
            </div>`;
          }
        }
      };
    }
  });

  return querySuggestionsPlugin;
};
